import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { compact } from 'lodash-es';
import { Observable, forkJoin, of, switchMap, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { environment } from '@env';
import { CustomHttpParamEncoder } from '@shared/encoder';
import { IS_EXTENDED_ERRORS, SKIP_ERROR_NOTIFICATION } from '@shared/interceptors';
import {
  CardAlert,
  CardAlertRaw,
  CardDispute,
  CardDisputeRaw,
  CardIssue,
  CardMerchant,
  CardMerchantListResponse,
  CardMerchantRaw,
  CardProduct,
  CardProductGeneralRestrictions,
  CardProductRestrictions,
  CardProductVelocityRestrictionCreateData,
  CardProductVelocityRestrictionsResponse,
  CardProductsResponse,
  CardReissue,
  CardRestriction,
  CardRestrictionRaw,
  CreateAlertData,
  CreateDisputeData,
  CreateRestrictionData,
  EntityFinancialAccountDetailsRaw,
  FinancialAccountDetails,
  FinancialAccountDetailsWithBalancesRaw,
  MccItem,
  MccItemRaw,
  RequestPageParams,
} from '@shared/models';
import { BackendService } from '@shared/services/backend.service';
import { AuditActions, selectFeatureFlagEnabledByKey } from '@shared/store';
import { ErrorUtils } from '@shared/utils';

import {
  mapCardAccountDetails,
  mapCardAlertItem,
  mapCardAlertItems,
  mapCardRestrictionItem,
  mapCardRestrictionItems,
} from './card-account-mapping-utils';

@Injectable({
  providedIn: 'root',
})
export class CardAccountService {
  constructor(
    private backendService: BackendService,
    private store: Store,
  ) {}

  private getSuppressErrorContext(): Observable<HttpContext> {
    return this.store.select(selectFeatureFlagEnabledByKey('suppress-disabled-features-errors')).pipe(
      take(1),
      map((shouldSuppress) => (shouldSuppress ? new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) : new HttpContext())),
    );
  }

  public issueCardAccount(cardIssueData: CardIssue): Observable<EntityFinancialAccountDetailsRaw> {
    return this.backendService
      .post<EntityFinancialAccountDetailsRaw>(`${environment.cardsEndpoint}/cards`, cardIssueData, {
        context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
      })
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public reissueCardAccount(cardAccountId: string, cardReissueData: CardReissue): Observable<EntityFinancialAccountDetailsRaw> {
    return this.backendService
      .post<EntityFinancialAccountDetailsRaw>(`${environment.cardsEndpoint}/cards/${cardAccountId}/reissue`, cardReissueData)
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public activateCardAccount({ cardAccountId }: { cardAccountId: string }): Observable<EntityFinancialAccountDetailsRaw> {
    return this.backendService
      .post<EntityFinancialAccountDetailsRaw>(`${environment.cardsEndpoint}/cards/${cardAccountId}/activate`)
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public getCardAccountDetails({
    businessAccountId,
    cardAccountId,
  }: {
    businessAccountId: string;
    cardAccountId: string;
  }): Observable<FinancialAccountDetails> {
    const params = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('embed', 'balances,cardProviderDetails');

    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<FinancialAccountDetailsWithBalancesRaw>(
          `${environment.financialAccountFlow}/business-accounts/${businessAccountId}/financial-accounts/${cardAccountId}`,
          { params, context },
        ),
      ),
      tap({
        next: (cardDetailsResponse) => {
          const { createdBy, updatedBy } = cardDetailsResponse;
          this.store.dispatch(AuditActions.loadTeamMembers({ ids: compact([createdBy, updatedBy]) }));
        },
      }),
      map((response) => mapCardAccountDetails(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardRestrictions({ financialAccountId }: { financialAccountId: string }): Observable<CardRestriction[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<CardRestrictionRaw[]>(`${environment.cardsEndpoint}/cards/${financialAccountId}/restrictions`, { context }),
      ),
      map((response) => mapCardRestrictionItems(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardRestrictionById({
    financialAccountId,
    restrictionId,
  }: {
    financialAccountId: string;
    restrictionId: string;
  }): Observable<CardRestriction> {
    return this.backendService
      .get<CardRestrictionRaw>(`${environment.cardsEndpoint}/cards/${financialAccountId}/restrictions/${restrictionId}`)
      .pipe(
        map((response) => mapCardRestrictionItem(response)),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public getCardAlerts({ financialAccountId }: { financialAccountId: string }): Observable<CardAlert[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<CardAlertRaw[]>(`${environment.cardsEndpoint}/cards/${financialAccountId}/alerts`, { context }),
      ),
      map((response) => mapCardAlertItems(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardAlertById({ financialAccountId, alertId }: { financialAccountId: string; alertId: string }): Observable<CardAlert> {
    return this.backendService.get<CardAlertRaw>(`${environment.cardsEndpoint}/cards/${financialAccountId}/alerts/${alertId}`).pipe(
      map((response) => mapCardAlertItem(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardDisputes({ financialAccountId }: { financialAccountId: string }): Observable<CardDispute[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<CardDisputeRaw[]>(`${environment.cardsEndpoint}/cards/${financialAccountId}/disputes`, { context }),
      ),
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardDisputeById({ financialAccountId, disputeId }: { financialAccountId: string; disputeId: string }): Observable<CardDispute> {
    return this.backendService.get<CardDisputeRaw>(`${environment.cardsEndpoint}/cards/${financialAccountId}/disputes/${disputeId}`).pipe(
      map((response) => response),
      catchError((errorRes) => throwError(() => errorRes.message)),
    );
  }

  public createDispute(financialAccountId: string, createDisputeData: CreateDisputeData): Observable<boolean> {
    return this.backendService
      .post(`${environment.cardsEndpoint}/cards/${financialAccountId}/disputes`, {
        ...createDisputeData,
      })
      .pipe(
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public createRestriction(financialAccountId: string, createRestrictionData: CreateRestrictionData): Observable<boolean> {
    return this.backendService
      .post(`${environment.cardsEndpoint}/cards/${financialAccountId}/restrictions`, { ...createRestrictionData })
      .pipe(
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public editRestriction(id: string, createRestrictionData: CreateRestrictionData): Observable<boolean> {
    return this.backendService
      .update(`${environment.cardsEndpoint}/cards/${createRestrictionData.financialAccountId}/restrictions/${id}`, {
        ...createRestrictionData,
      })
      .pipe(
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public createAlert(createAlertData: CreateAlertData): Observable<boolean> {
    return this.backendService
      .post(
        `${environment.cardsEndpoint}/cards/${createAlertData.financialAccountId}/alerts`,
        { ...createAlertData },
        { context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
      )
      .pipe(
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public editAlert(id: string, createAlertData: CreateAlertData): Observable<boolean> {
    return this.backendService
      .update(`${environment.cardsEndpoint}/cards/${createAlertData.financialAccountId}/alerts/${id}`, { ...createAlertData })
      .pipe(
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public getCardProducts({ requestParams }: { requestParams?: RequestPageParams }): Observable<CardProductsResponse> {
    const { page, size = 10, sortParams = { key: 'createdAt', sortDir: 'desc' } } = requestParams ?? {};
    let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });

    params = params.append('size', size);

    if (page) {
      params = params.append('page', page);
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    const context = new HttpContext().set(SKIP_ERROR_NOTIFICATION, true);

    return this.backendService
      .get<CardProductsResponse>(`${environment.cardsEndpoint}/products`, { params, context })
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public getMccItems(): Observable<MccItem[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) => this.backendService.get<MccItemRaw[]>(`${environment.cardsEndpoint}/mcc`, { context })),
      map((result) => {
        return result.map((item) => ({
          label: `${item.mcc}: ${item.mccDescription}`,
          value: `${item.mcc}`,
        }));
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardProductDetails({ productId }: { productId: string }): Observable<CardProduct> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) => this.backendService.get<CardProduct>(`${environment.cardsEndpoint}/products/${productId}`, { context })),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getCardProductRestrictions({ productId }: { productId: string }): Observable<CardProductRestrictions> {
    return forkJoin({
      mccItems: this.getMccItems(),
      merchantItems: this.getMerchants({ productId }),
      generalRestrictions: this.backendService
        .get<CardProductGeneralRestrictions>(`${environment.cardsEndpoint}/products/${productId}/general-restrictions`)
        .pipe(
          catchError((error) => {
            ErrorUtils.catchError(error);
            return of(null);
          }),
        ),

      velocityRestrictions: this.backendService
        .get<CardProductVelocityRestrictionsResponse>(`${environment.cardsEndpoint}/products/${productId}/velocity-restrictions`)
        .pipe(
          catchError((error) => {
            ErrorUtils.catchError(error);
            return of();
          }),
        ),
    }).pipe(
      map((results) => {
        const { mccItems, merchantItems, generalRestrictions } = results;
        const generalRestrictionsMapped = {
          ...generalRestrictions!,
          merchantCategoryList: {
            ...generalRestrictions!.merchantCategoryList,
            merchantCategoryCodeNames: compact(
              generalRestrictions!.merchantCategoryList.merchantCategoryCodes.map(
                (merchantCode) => mccItems.find((mcc) => mcc.value === merchantCode.toString().padStart(4, '0'))?.label,
              ) ?? [],
            ),
          },
          merchantList: {
            ...generalRestrictions!.merchantList,
            merchantNames: compact(
              generalRestrictions!.merchantList.merchantIds.map(
                (merchantId) => merchantItems.find((merchant) => merchant.merchantId === merchantId)?.legalName,
              ) ?? [],
            ),
          },
        };
        return {
          generalRestrictions: generalRestrictionsMapped,
          velocityRestrictions: results.velocityRestrictions.content ?? [],
        };
      }),
    );
  }

  public createCardProductVelocityRestriction({
    productId,
    createVelocityRestrictionData,
  }: {
    productId: string;
    createVelocityRestrictionData: CardProductVelocityRestrictionCreateData;
  }): Observable<boolean> {
    return this.backendService
      .post(`${environment.cardsEndpoint}/products/${productId}/velocity-restrictions`, {
        ...createVelocityRestrictionData,
      })
      .pipe(
        map(() => true),
        catchError((errorRes) => throwError(() => errorRes)),
      );
  }

  public getMerchants({ productId }: { productId: string }): Observable<CardMerchant[]> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<CardMerchantListResponse>(`${environment.cardsEndpoint}/products/${productId}/merchants`, { context }),
      ),
      map(
        (response) =>
          response?.content?.map((item) => ({
            ...item,
            city: item.merchantAddresses?.[0].city,
            state: item.merchantAddresses?.[0].state,
            countryCode: item.merchantAddresses?.[0].countryCode,
          })) ?? [],
      ),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public getMerchantById({ productId, merchantId }: { productId: string; merchantId: string }): Observable<CardMerchant> {
    return this.getSuppressErrorContext().pipe(
      switchMap((context) =>
        this.backendService.get<CardMerchantRaw>(`${environment.cardsEndpoint}/products/${productId}/merchants/${merchantId}`, { context }),
      ),
      map((response) => ({ ...response })),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public updateGeneralRestrictions({
    productId,
    generalRestrictions,
  }: {
    productId: string;
    generalRestrictions: CardProductGeneralRestrictions;
  }): Observable<boolean> {
    return this.backendService.update(`${environment.cardsEndpoint}/products/${productId}/general-restrictions`, generalRestrictions).pipe(
      map(() => true),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }
}
